import FullScreenLoadingOverlay from '@src/components/common/FullScreenLoadingOverlay';
import { useUserIdentity } from '@src/stores-v2/StoreContext';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import {
  matchPath,
  Navigate,
  Outlet,
  useLocation,
  useSearchParams,
} from 'react-router-dom';

/**
 * The only routes a translation reviewer may reach. Everything else — Home,
 * Creator Studio, Speakers, Brand Kit, Settings, B-roll upload, `/` — bounces
 * to Story Manager.
 *
 * This is an allowlist on purpose: as a denylist of wrapped routes, every route
 * added later would be reachable by reviewers until someone remembered to guard
 * it. Reviewers get Story Manager (to pick a story) and Content Studio (the
 * clip list, its preview modal, and the Transcript & Speakers tab).
 */
const REVIEWER_ROUTES = ['/story-manager', '/content'];

/**
 * How long a restricted route waits for LaunchDarkly before rendering anyway.
 *
 * Bounded on purpose. LaunchDarkly reports every flag as `undefined` until it
 * identifies, which is indistinguishable from "reviewer mode off", so without a
 * wait a reviewer deep-linking to `/creator` mounts Creator Studio — renderer
 * iframe, story load and all — for the frames before the redirect fires. But
 * LaunchDarkly is routinely blocked by ad blockers and corporate proxies, and
 * `initialized` then never flips: waiting on it indefinitely would turn a
 * dark-launched reviewer mode into an app-wide hang. After the deadline we
 * render and fall back to redirecting if the flags ever do arrive.
 */
const FLAG_WAIT_MS = 1500;

/**
 * Pathless layout route that gates the whole router for translation-reviewer
 * mode. Wraps every route once (see `EditorEntry`) rather than per route, so
 * the reduced experience has a single definition.
 *
 * Client-side UX gating only — it hides screens, it does not authorize.
 */
export const ReviewerRouteGuard = observer(() => {
  const userIdentity = useUserIdentity();
  const { enableTranslationReviewerMode, initialized: flagsInitialized } =
    useFlagsCombination(userIdentity.currentRole);
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const [waitedForFlags, setWaitedForFlags] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setWaitedForFlags(true), FLAG_WAIT_MS);
    return () => clearTimeout(timer);
  }, []);

  const allowed = REVIEWER_ROUTES.some(route => matchPath(route, pathname));

  // Reviewer-reachable routes never wait — they are what reviewer mode resolves
  // to, so the flags can't change what renders there.
  // Same overlay and copy as EditorEntry's store-init gate, so a slow
  // LaunchDarkly reads as one continuous load rather than a second screen.
  if (!allowed && !flagsInitialized && !waitedForFlags) {
    return <FullScreenLoadingOverlay text="Loading…" />;
  }

  if (enableTranslationReviewerMode && !allowed) {
    return (
      <Navigate to={`/story-manager?${searchParams.toString()}`} replace />
    );
  }

  return <Outlet />;
});
